<template>
  <div v-if="!participant.upcoming_task && participant.assigned == 1">
    <button type="button" class="btn btn-xs btn-default" @click="showTaskModal(participant)">
      Create Task
    </button>
  </div>
  <div 
    v-if="participant.upcoming_task" 
    class="transition-all" 
    :class="{updating: updatingWindow}"
    >
    <a :href="'/tasks/'+participant.upcoming_task.id">
      {{ participant.upcoming_task.task_type.name }} {{ participant.upcoming_task.visit_window_id }}
    </a>
    <small v-if="participant.upcoming_task.task_type_id == 15" class="text-muted">
      ({{ (participant.upcoming_task.date_due) ? participant.upcoming_task.date_due.format('MM/DD/YYYY') : 'WTF' }})
    </small>
    <div style="display: inline-block; position: relative;">
      <button 
        type="button" 
        class="btn btn-xs btn-default" 
        title="Cancel, not needed"
        v-on:click="cancelTask"
        :disabled="cancelingTask"
        >
          {{ cancelingTask ? 'Canceling...' : 'Cancel' }}
      </button>
      <button
        type="button"
        class="btn btn-xs btn-default"
        title="Change the task's visit window"
        v-on:click="launchWindowUpdate(participant)"
        :disabled="cancelingTask"
        >
          Update window
      </button>
    </div>
  </div>        

  <modal
    v-model:showModal="showWindowModal"
    title="Update the visit window"
    @closed="error=null; showWindowModal=false;"
    >
    <template v-slot:title>Update substudy task's visit window for {{currentParticipant.full_name}}</template>
    <template v-slot:body>
      <div class="alert alert-danger" v-if="error">
        {{error}}
      </div>
      <div class="form-group" >
        <label for="new-window-id">Visit Window</label>
        <select 
          v-model="newTaskVisitWindowId" 
          id="new-window-id" 
          class="form-control"
          :disabled="updatingWindow"
          >
          <option 
            v-for="(window) in visitWindows"
            :key="window.id"
            :value="window.id"
          >{{window.name}}</option>
        </select>
      </div>
      <div class="clearfix">
        <button 
          type="button" 
          class="btn btn-primary pull-right" 
          @click="updateVisitWindow()"
          :disabled="updatingWindow"
          >
            {{updatingWindow ? 'Updating...' : 'Update Window'}}
        </button>
      </div>
    </template>
  </modal>

  <modal 
    v-model:showModal="showNewTaskModal" 
    @closed="error=null; showNewTaskModal=false;"
    :title="modalTitle"
    >
    <template v-slot:body>
      <div class="alert alert-danger" v-show="error">
        {{error}}
      </div>
      <div class="form-group">
        <label for="new-window-id">Visit Window</label>
        <select v-model="newTaskVisitWindowId" id="new-window-id" class="form-control" :disabled="creatingTask">
          <option 
            v-for="(window) in visitWindows"
            :key="window.id"
            :value="window.id"
          >{{window.name}}</option>
        </select>
      </div>
      <button type="button" class="btn btn-default" @click="createNewTask" :disabled="creatingTask">
        {{creatingTask ? 'Creating...' : 'Create Task'}}
      </button>
    </template>
  </modal>
</template>

<script setup>
  import { ref, computed } from 'vue'
	import Modal from '../Modal';
	import SubstudyResource from '../../resources/SubstudyResource';
	import moment from 'moment';

  const props = defineProps(['participant', 'visitWindows', 'substudy'])
  const emit = defineEmits(['taskcreated', 'taskupdated', 'taskcanceled'])

	const showWindowModal = ref(false)
	const showNewTaskModal = ref(false)
  const newTaskVisitWindowId = ref(null)
  const currentParticipant = ref({})
  const error = ref(null)
  const creatingTask = ref(false)
  const updatingWindow = ref(false)
  const cancelingTask = ref(false)

  const modalTitle = computed(() => {
    return 'Create a task for a visit window for '+props.participant.full_name
  })

	function launchWindowUpdate(participantRow) {
    newTaskVisitWindowId.value = props.participant.upcoming_task.visit_window_id
    error.value = null;
		showWindowModal.value = true
	}

	function showTaskModal(participant) {
		showNewTaskModal.value = true;
    error.value = null;
		currentParticipant.value = participant
	}

	function createNewTask () {
    creatingTask.value = true;
		const data = {
			'owner_id': props.participant.id,
			'owner_type': 'App\\Models\\Participant',
			'visit_window_id': newTaskVisitWindowId.value,
			'task_type_id': 15,
			'date_due': moment(props.visitWindows.find((item) => item.id == newTaskVisitWindowId.value).closes_at).format('YYYY-MM-DD'),
			'data': {
				'substudy_id': props.substudy.id,
				'visit_window_id': newTaskVisitWindowId.value
			}
		};

		window.axios.post('/api/tasks', data)
			.then(taskResponse => {
        SubstudyResource.participants.find(props.substudy.id, props.participant.id)
          .then(response => {
            emit('taskcreated', {participant: response.data, task: taskResponse.data});
            showNewTaskModal.value = false;
            creatingTask.value = false;
          })
          .catch(error => {
            console.log(error);
            creatingTask.value = false;
          })
			})
			.catch(error => {
				error.value = error.response.data.message;
        creatingTask.value = false;
			})
	}

	function updateVisitWindow() {
		updatingWindow.value = true;
		SubstudyResource.participants
			.updateTaskVisitWindow(props.participant.upcoming_task.data.substudy_id, props.participant, newTaskVisitWindowId.value)
			.then(response => {
        updatingWindow.value = false;
        showWindowModal.value = false;
				emit('taskupdated', {
          participant: response.data,
          visitWindowId: newTaskVisitWindowId.value
        });
			})
			.catch((error) => {
				if (error.response.status == 422) {
					error.value = error.response.data.message;
				}
				updatingWindow.value = false;
			})
  }

  function cancelTask() {
		cancelingTask.value = true;
		SubstudyResource.participants.cancelUpcomingTask(props.substudy.id, props.participant)
			.then((response) => {
        SubstudyResource.participants.find(props.substudy.id, props.participant.id)
          .then(response => {
            emit('taskcanceled', response.data);
            cancelingTask.value = false;
          })
          .catch(error => {
            console.log(error);
            cancelingTask.value = false;
          })
			})
			.catch(function (error) {
        alert('Failed to cancel task');
        cancelingTask.value = false;
			})
  }

	function clearVisitWindowForm() {
		newVisitTaskWindowId.value = null
	}
</script>

<style scoped>
  .transition-all {
    transition: all .5s;
  }
  .updating {
    background: lightgoldenrodyellow
  }
</style>